import { supabase } from './supabase';
import {
  ATIVACAO_FAIXAS,
  getRedeParceiroRate,
  avaliarSla,
  avaliarCarteira,
  calcularAtivacaoV2,
  resolverMultiplicadorNegociacao,
  SLA_PENALIDADE_FATOR,
  CLAWBACK_DIAS,
  RECORRENCIA_RS_KWH,
  RECORRENCIA_MESES_CARENCIA,
  SAFRA_BONUS,
  SlaDetalhe,
} from './politicaComercial';
import type { NegociacaoAprovadaMes } from './politicaComercial';
import type { RepassePosVenda } from './comissaoIndicacaoPosVenda';

// A parte pura (tipo da negociação, multiplicador e conta de ativação) mora em
// politicaComercial.ts — reexportada aqui para quem já importava deste arquivo.
export { resolverMultiplicadorNegociacao };
export type { NegociacaoAprovadaMes };

// ==========================================
// MOTOR DA NOVA COMISSÃO — VENDEDOR EXTERNO (política v2, jul/2026)
// Componentes: ativação R$/kWh × multiplicador por negociação, bônus de
// threshold, bonificação da rede de parceiros e clawback de 90 dias.
// Recorrência (Fase 3) entra como componente separado no fechamento.
// ==========================================

export interface RedeParceiroVolume {
  parceiroId: string;
  volumeKwh: number;
  negs: NegociacaoAprovadaMes[];
}

/**
 * SLA de esforço no CRM (Filtro 1) medido das fontes reais:
 * visitas = tarefas concluídas no mês (fonte única da visita);
 * pós-venda = visita de pós-venda concluída (tipo='pos_venda') OU tarefa concluída de
 *   negociação no pipeline Pós-Venda — união, para o critério antigo não parar de contar
 *   de um mês para o outro quando o fluxo novo entrou (spec pós-venda §9);
 * fechamentos = negociações com termo assinado no mês;
 * parceiros = captados no mês (captado_por_id) — "manutenção" conta via rede produzindo.
 *
 * ⚠️ Esta lógica é duplicada em supabase/functions/get-minha-comissao-externa/index.ts
 * (leitura privilegiada da tela "Minha Comissão"). As duas precisam mudar juntas.
 */
export async function coletarSlaVendedor(
  vendedorId: string,
  mes: number,
  ano: number,
  parceirosProduzindoNoMes: number
): Promise<SlaDetalhe> {
  const mesStr = String(mes).padStart(2, '0');
  const lastDay = new Date(ano, mes, 0).getDate();
  const inicio = `${ano}-${mesStr}-01T00:00:00`;
  const fim = `${ano}-${mesStr}-${lastDay}T23:59:59`;

  const [tarefasRes, fechamentosRes, captadosRes, pipelineRes] = await Promise.all([
    supabase
      .from('tarefas')
      .select('id, tipo, negociacoes(pipeline_id)')
      .eq('responsavel_id', vendedorId)
      .eq('status', 'concluido')
      .gte('data_conclusao', inicio)
      .lte('data_conclusao', fim)
      .limit(2000),
    supabase
      .from('negociacoes')
      .select('id', { count: 'exact', head: true })
      .eq('responsavel_id', vendedorId)
      .gte('data_assinatura_termo', inicio)
      .lte('data_assinatura_termo', fim),
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('role', 'parceiro')
      .eq('captado_por_id', vendedorId)
      .gte('created_at', inicio)
      .lte('created_at', fim),
    supabase.from('pipelines').select('id, name').eq('name', 'Pós-Venda').maybeSingle(),
  ]);

  const tarefas = (tarefasRes.data || []) as any[];
  const posVendaPipelineId = (pipelineRes.data as any)?.id ?? null;
  const posVenda = tarefas.filter(t => {
    if (t.tipo === 'pos_venda') return true;
    if (!posVendaPipelineId) return false;
    const negs = t.negociacoes;
    const pid = Array.isArray(negs) ? negs[0]?.pipeline_id : negs?.pipeline_id;
    return pid === posVendaPipelineId;
  }).length;

  const captados = captadosRes.count || 0;
  return {
    visitas: tarefas.length,
    posVenda,
    fechamentos: fechamentosRes.count || 0,
    parceiros: Math.max(captados, parceirosProduzindoNoMes),
  };
}

/**
 * Clawback de 90 dias: negociações de fechamentos v2 anteriores deste vendedor
 * que viraram 'perdido' em menos de CLAWBACK_DIAS após a ativação. Marca a linha
 * como estornada e devolve o total a debitar na competência atual.
 */
export async function coletarClawbacks(vendedorId: string, mes: number, ano: number): Promise<{
  total: number;
  itens: Array<{ negociacaoId: number; valor: number }>;
}> {
  const { data: fechamentosAnteriores } = await supabase
    .from('comissao_vendedores')
    .select('id, mes, ano')
    .eq('vendedor_id', vendedorId)
    .eq('politica', 'v2')
    .or(`ano.lt.${ano},and(ano.eq.${ano},mes.lt.${mes})`);

  const ids = (fechamentosAnteriores || []).map((f: any) => f.id);
  if (ids.length === 0) return { total: 0, itens: [] };

  const { data: linhas } = await supabase
    .from('comissao_vendedor_negociacoes')
    .select('id, negociacao_id, valor_ativacao, created_at, clawback_status, negociacao:negociacoes(id, status, updated_at)')
    .in('comissao_vendedor_id', ids)
    .is('clawback_status', null)
    .gt('valor_ativacao', 0);

  const itens: Array<{ negociacaoId: number; valor: number; linhaId: number }> = [];
  for (const l of (linhas || []) as any[]) {
    const neg = Array.isArray(l.negociacao) ? l.negociacao[0] : l.negociacao;
    if (!neg || neg.status !== 'perdido') continue;
    const ativadaEm = new Date(l.created_at).getTime();
    const perdidaEm = new Date(neg.updated_at || Date.now()).getTime();
    const dias = (perdidaEm - ativadaEm) / (1000 * 60 * 60 * 24);
    if (dias <= CLAWBACK_DIAS) {
      itens.push({ negociacaoId: l.negociacao_id, valor: Number(l.valor_ativacao) || 0, linhaId: l.id });
    }
  }

  for (const item of itens) {
    await supabase
      .from('comissao_vendedor_negociacoes')
      .update({ clawback_status: 'estornado', clawback_em: new Date().toISOString() })
      .eq('id', (item as any).linhaId);
  }

  return {
    total: itens.reduce((s, i) => s + i.valor, 0),
    itens: itens.map(({ negociacaoId, valor }) => ({ negociacaoId, valor })),
  };
}

// ==========================================
// RECORRÊNCIA DA CARTEIRA (Fase 3)
// ==========================================

export interface RecorrenciaResultado {
  valor: number;
  destravada: boolean;
  carteiraDetalhe: {
    inadimplenciaPct: number;
    descontoMedioPct: number;
    primeiraFaturaPagaPct: number;
    churnPct: number;
    reclamacoesControladas: boolean;
    itens: Array<{ label: string; ok: boolean; valor: string }>;
    ucsAtivas: number;
    ucsCanceladas: number;
    cnpjsElegiveis: number;
    cnpjsPagantesNoMes: number;
    volumeElegivelKwh: number;
    ucsSemFaturaNoMes: string[]; // cobertura do match faturas↔UC (correção manual)
  };
}

/**
 * Recorrência do vendedor externo em duas camadas:
 * 1. por CNPJ: só gera no mês em que a fatura foi paga E a partir do 3º mês
 *    distinto de pagamento daquele cliente;
 * 2. saúde da carteira: os indicadores precisam estar no padrão, senão a
 *    recorrência trava para a carteira inteira no mês.
 * Faturas são localizadas por uc_codigo = numero_uc/outro_numero_uc (match
 * sabidamente incompleto — UCs sem fatura no mês são listadas para correção).
 */
export async function coletarRecorrencia(
  vendedorId: string,
  mes: number,
  ano: number,
  opts?: { reclamacoesControladas?: boolean }
): Promise<RecorrenciaResultado> {
  const mesStr = String(mes).padStart(2, '0');
  const lastDay = new Date(ano, mes, 0).getDate();
  const inicioMes = `${ano}-${mesStr}-01T00:00:00`;
  const fimMes = `${ano}-${mesStr}-${lastDay}T23:59:59`;
  const reclamacoesControladas = opts?.reclamacoesControladas ?? true;

  const vazio: RecorrenciaResultado = {
    valor: 0,
    destravada: false,
    carteiraDetalhe: {
      inadimplenciaPct: 0, descontoMedioPct: 0, primeiraFaturaPagaPct: 0, churnPct: 0,
      reclamacoesControladas, itens: [], ucsAtivas: 0, ucsCanceladas: 0,
      cnpjsElegiveis: 0, cnpjsPagantesNoMes: 0, volumeElegivelKwh: 0, ucsSemFaturaNoMes: [],
    },
  };

  // Carteira: UCs das negociações do vendedor
  const { data: ucsData } = await supabase
    .from('unidades_consumidoras')
    .select('id, numero_uc, outro_numero_uc, consumo_medio_kwh, cpf_cnpj_cliente, situacao, desconto_tarifa, negociacoes!inner(responsavel_id)')
    .eq('negociacoes.responsavel_id', vendedorId)
    .limit(2000);

  const ucs = (ucsData || []) as any[];
  if (ucs.length === 0) return vazio;

  const ucsCanceladas = ucs.filter(u => u.situacao === 'Cancelada');
  const ucsAtivas = ucs.filter(u => u.situacao !== 'Cancelada');
  if (ucsAtivas.length === 0) return vazio;

  // Faturas da carteira (por numero_uc e outro_numero_uc)
  const codes = [...new Set(ucsAtivas.flatMap(u => [u.numero_uc, u.outro_numero_uc]).filter(Boolean))] as string[];
  const faturas: any[] = [];
  for (let i = 0; i < codes.length; i += 150) {
    const chunk = codes.slice(i, i + 150);
    const { data } = await supabase
      .from('faturas')
      .select('uc_codigo, status_boleto_vigor, data_pgto_boleto, data_vencimento')
      .in('uc_codigo', chunk)
      .limit(5000);
    faturas.push(...(data || []));
  }

  const faturasPorCode = new Map<string, any[]>();
  for (const f of faturas) {
    const list = faturasPorCode.get(f.uc_codigo) || [];
    list.push(f);
    faturasPorCode.set(f.uc_codigo, list);
  }
  const faturasDaUc = (u: any): any[] => [
    ...(faturasPorCode.get(u.numero_uc) || []),
    ...(u.outro_numero_uc ? (faturasPorCode.get(u.outro_numero_uc) || []) : []),
  ];

  const fimMesDate = new Date(fimMes);
  const inicioMesDate = new Date(inicioMes);
  const isPaga = (f: any) => (f.status_boleto_vigor === 'Pago' || f.status_boleto_vigor === 'Quitado') && f.data_pgto_boleto;

  // Camada 1 — por CNPJ
  const porCnpj = new Map<string, { ucs: any[]; mesesPagos: Set<string>; pagouNoMes: boolean }>();
  for (const u of ucsAtivas) {
    const cnpj = (u.cpf_cnpj_cliente || '').replace(/\D/g, '') || `uc:${u.numero_uc}`;
    let entry = porCnpj.get(cnpj);
    if (!entry) {
      entry = { ucs: [], mesesPagos: new Set(), pagouNoMes: false };
      porCnpj.set(cnpj, entry);
    }
    entry.ucs.push(u);
    for (const f of faturasDaUc(u)) {
      if (!isPaga(f)) continue;
      const dt = new Date(f.data_pgto_boleto);
      if (dt > fimMesDate) continue; // ignora pagamentos após a competência
      entry.mesesPagos.add(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
      if (dt >= inicioMesDate) entry.pagouNoMes = true;
    }
  }

  let volumeElegivel = 0;
  let cnpjsElegiveis = 0;
  let cnpjsPagantesNoMes = 0;
  for (const [, entry] of porCnpj) {
    if (entry.pagouNoMes) cnpjsPagantesNoMes++;
    if (entry.pagouNoMes && entry.mesesPagos.size >= RECORRENCIA_MESES_CARENCIA) {
      cnpjsElegiveis++;
      volumeElegivel += entry.ucs.reduce((s, u) => s + (Number(u.consumo_medio_kwh) || 0), 0);
    }
  }

  // Camada 2 — saúde da carteira
  const ucsComFatura = ucsAtivas.filter(u => faturasDaUc(u).length > 0);
  const ucsComVencido = ucsAtivas.filter(u => faturasDaUc(u).some(f =>
    f.status_boleto_vigor === 'Vencido' && f.data_vencimento && new Date(f.data_vencimento) <= fimMesDate
  ));
  const ucsComPrimeiraPaga = ucsComFatura.filter(u => faturasDaUc(u).some(isPaga));
  const ucsComDesconto = ucsAtivas.filter(u => u.desconto_tarifa != null);

  const indicadores = {
    inadimplenciaPct: ucsAtivas.length > 0 ? (ucsComVencido.length / ucsAtivas.length) * 100 : 0,
    descontoMedioPct: ucsComDesconto.length > 0
      ? (ucsComDesconto.reduce((s, u) => s + (Number(u.desconto_tarifa) || 0), 0) / ucsComDesconto.length) * 100
      : 0,
    primeiraFaturaPagaPct: ucsComFatura.length > 0 ? (ucsComPrimeiraPaga.length / ucsComFatura.length) * 100 : 100,
    churnPct: ucs.length > 0 ? (ucsCanceladas.length / ucs.length) * 100 : 0,
    reclamacoesControladas,
  };
  const saude = avaliarCarteira(indicadores);

  // Cobertura do match faturas↔UC: UCs ativas sem nenhuma fatura paga/aberta no mês
  const ucsSemFaturaNoMes = ucsAtivas
    .filter(u => !faturasDaUc(u).some(f => {
      const ref = f.data_pgto_boleto || f.data_vencimento;
      if (!ref) return false;
      const dt = new Date(ref);
      return dt >= inicioMesDate && dt <= fimMesDate;
    }))
    .map(u => u.numero_uc);

  return {
    valor: saude.destravada ? volumeElegivel * RECORRENCIA_RS_KWH : 0,
    destravada: saude.destravada,
    carteiraDetalhe: {
      ...indicadores,
      itens: saude.itens,
      ucsAtivas: ucsAtivas.length,
      ucsCanceladas: ucsCanceladas.length,
      cnpjsElegiveis,
      cnpjsPagantesNoMes,
      volumeElegivelKwh: volumeElegivel,
      ucsSemFaturaNoMes,
    },
  };
}

// ==========================================
// BÔNUS DE 1ª FATURA (safra) — ajuste Bruno 13/07/2026
// ==========================================

export interface BonusSafraResultado {
  bonus: number;
  detalhe: {
    safraMes: number;
    safraAno: number;
    totalClientes: number;
    pagosNoMes: number;
    pct: number;
    clientes: Array<{ negociacaoId: number; pago: boolean }>;
  } | null;
}

/**
 * Bônus da safra de 1ª fatura, pago na competência SEGUINTE à do envio do
 * 1º boleto: dos clientes (negociações) do vendedor cujo primeiro boleto foi
 * enviado no mês anterior, ≥90% pagos dentro daquele mês → R$ 250; 100% → R$ 500.
 * Faturas localizadas por uc_codigo = numero_uc/outro_numero_uc.
 */
export async function coletarBonusSafra(vendedorId: string, mes: number, ano: number): Promise<BonusSafraResultado> {
  const safraMes = mes === 1 ? 12 : mes - 1;
  const safraAno = mes === 1 ? ano - 1 : ano;
  const safraStr = String(safraMes).padStart(2, '0');
  const safraLastDay = new Date(safraAno, safraMes, 0).getDate();
  const safraInicio = new Date(`${safraAno}-${safraStr}-01T00:00:00`);
  const safraFim = new Date(`${safraAno}-${safraStr}-${safraLastDay}T23:59:59`);

  const vazio: BonusSafraResultado = { bonus: 0, detalhe: null };

  const { data: ucsData } = await supabase
    .from('unidades_consumidoras')
    .select('numero_uc, outro_numero_uc, negociacao_id, negociacoes!inner(responsavel_id)')
    .eq('negociacoes.responsavel_id', vendedorId)
    .limit(2000);

  const ucs = (ucsData || []) as any[];
  if (ucs.length === 0) return vazio;

  const codes = [...new Set(ucs.flatMap(u => [u.numero_uc, u.outro_numero_uc]).filter(Boolean))] as string[];
  const faturas: any[] = [];
  for (let i = 0; i < codes.length; i += 150) {
    const chunk = codes.slice(i, i + 150);
    const { data } = await supabase
      .from('faturas')
      .select('uc_codigo, boleto_emissao_data, data_vencimento, status_boleto_vigor, data_pgto_boleto')
      .in('uc_codigo', chunk)
      .limit(5000);
    faturas.push(...(data || []));
  }
  if (faturas.length === 0) return vazio;

  const negPorCode = new Map<string, number>();
  for (const u of ucs) {
    if (u.numero_uc) negPorCode.set(u.numero_uc, u.negociacao_id);
    if (u.outro_numero_uc) negPorCode.set(u.outro_numero_uc, u.negociacao_id);
  }

  // 1º boleto de cada negociação (menor data de emissão/vencimento entre as faturas dela)
  const primeiroBoletoPorNeg = new Map<number, { data: Date; pago: boolean }>();
  for (const f of faturas) {
    const negId = negPorCode.get(f.uc_codigo);
    if (!negId) continue;
    const emissao = f.boleto_emissao_data || f.data_vencimento;
    if (!emissao) continue;
    const dt = new Date(emissao);
    const atual = primeiroBoletoPorNeg.get(negId);
    if (!atual || dt < atual.data) {
      const pgto = f.data_pgto_boleto ? new Date(f.data_pgto_boleto) : null;
      const pago = (f.status_boleto_vigor === 'Pago' || f.status_boleto_vigor === 'Quitado')
        && !!pgto && pgto >= safraInicio && pgto <= safraFim;
      primeiroBoletoPorNeg.set(negId, { data: dt, pago });
    }
  }

  // Safra = negociações cujo 1º boleto foi enviado no mês anterior à competência
  const clientes: Array<{ negociacaoId: number; pago: boolean }> = [];
  for (const [negId, info] of primeiroBoletoPorNeg) {
    if (info.data >= safraInicio && info.data <= safraFim) {
      clientes.push({ negociacaoId: negId, pago: info.pago });
    }
  }
  if (clientes.length === 0) return vazio;

  const pagos = clientes.filter(c => c.pago).length;
  const pct = (pagos / clientes.length) * 100;
  const bonus = pct >= 100 ? SAFRA_BONUS.pct100 : pct >= 90 ? SAFRA_BONUS.pct90 : 0;

  return {
    bonus,
    detalhe: { safraMes, safraAno, totalClientes: clientes.length, pagosNoMes: pagos, pct, clientes },
  };
}

export interface FechamentoV2 {
  comissaoData: Record<string, any>;
  /** Vendas indicadas pelo pós-venda: a parte que vai para ele (linha própria na Folha, #6236). */
  repassesPosVenda: RepassePosVenda[];
  detalhes: Array<{
    negociacao_id: number;
    consumo_medio_kwh: number;
    multiplicador_final: number;
    valor_ativacao: number;
    parcela_1_valor: number;
    parcela_2_valor: number;
    is_grupo_a: boolean;
    grupo_a_status?: string;
    valor_bonus: number;
  }>;
}

/**
 * Fechamento mensal completo da política v2 para um vendedor externo.
 * Recorrência EM ANÁLISE (Bruno 13/07/2026) — não entra no cálculo enquanto
 * RECORRENCIA_ATIVA=false; o bônus de safra da 1ª fatura entra no lugar.
 */
export async function calcularFechamentoExternoV2(params: {
  vendedorId: string;
  mes: number;
  ano: number;
  negs: Array<NegociacaoAprovadaMes & { is_grupo_a: boolean }>;
  redeParceiros: RedeParceiroVolume[];
  salarioBase: number;
  metaKwh: number;
  tarifaValor: number;
  tarifaId: number | null;
}): Promise<FechamentoV2> {
  const { vendedorId, mes, ano, negs, redeParceiros, salarioBase, tarifaValor, tarifaId } = params;

  // 1. Ativação: R$/kWh pela FAIXA DE ATINGIMENTO DA META × multiplicador de cada negociação.
  // Grupo A continua fora do cálculo automático (aprovação manual do gerente).
  // A conta é a mesma que a barra "Meu Trabalho" do CRM mostra (calcularAtivacaoV2).
  const ativacao = calcularAtivacaoV2(negs, params.metaKwh);
  const metaKwh = ativacao.metaKwh;
  const volumeTotal = ativacao.volumeTotal;
  const rate = ativacao.rate;
  let valorAtivacao = ativacao.valorAtivacao;
  const detalhes: FechamentoV2['detalhes'] = [];
  const memoriaAtivacao: any[] = [];
  for (const item of ativacao.itens) {
    detalhes.push({
      negociacao_id: item.negociacao_id,
      consumo_medio_kwh: item.kwh,
      multiplicador_final: item.mult,
      valor_ativacao: item.valor,
      parcela_1_valor: item.valor / 2,
      parcela_2_valor: item.valor / 2,
      is_grupo_a: item.is_grupo_a,
      grupo_a_status: item.is_grupo_a ? 'pendente_aprovacao_gerente' : undefined,
      valor_bonus: 0,
    });
    memoriaAtivacao.push({ negociacao_id: item.negociacao_id, kwh: item.kwh, rate, mult: item.mult, origem: item.origem, valor: item.valor, grupo_a: item.is_grupo_a });
  }

  // 2. Bônus de threshold (Filtro 2)
  let valorThreshold = ativacao.valorThreshold;

  // 3. SLA (Filtro 1) — não cumpriu → ativação e threshold caem pela metade
  const parceirosProduzindo = redeParceiros.filter(p => p.volumeKwh > 0).length;
  const slaDetalhe = await coletarSlaVendedor(vendedorId, mes, ano, parceirosProduzindo);
  const sla = avaliarSla(slaDetalhe);
  if (!sla.cumprido) {
    valorAtivacao *= SLA_PENALIDADE_FATOR;
    valorThreshold *= SLA_PENALIDADE_FATOR;
    for (const d of detalhes) {
      d.valor_ativacao *= SLA_PENALIDADE_FATOR;
      d.parcela_1_valor = d.valor_ativacao / 2;
      d.parcela_2_valor = d.valor_ativacao / 2;
    }
  }

  // 4. Bonificação da rede de parceiros (análise individual por negociação do parceiro)
  let valorRede = 0;
  const memoriaRede: any[] = [];
  for (const parceiro of redeParceiros) {
    const rateParceiro = getRedeParceiroRate(parceiro.volumeKwh);
    for (const neg of parceiro.negs) {
      const kwh = Number(neg.consumo_medio_kwh) || 0;
      const { mult } = resolverMultiplicadorNegociacao(neg);
      const valor = kwh * rateParceiro * mult;
      valorRede += valor;
      memoriaRede.push({ parceiro_id: parceiro.parceiroId, negociacao_id: neg.id, kwh, rate: rateParceiro, mult, valor });
    }
  }

  // 5. Bônus de safra de 1ª fatura (competência seguinte à do 1º boleto)
  const safra = await coletarBonusSafra(vendedorId, mes, ano);

  // 6. Clawback de 90 dias (débito de fechamentos anteriores)
  const clawback = await coletarClawbacks(vendedorId, mes, ano);
  const valorClawback = -clawback.total;

  const percentualAtingimento = metaKwh > 0 ? (volumeTotal / metaKwh) * 100 : 0;
  const valorTotal = salarioBase + valorAtivacao + valorThreshold + valorRede + safra.bonus + valorClawback;

  return {
    comissaoData: {
      vendedor_id: vendedorId,
      mes,
      ano,
      politica: 'v2',
      total_kwh_vendido: volumeTotal,
      meta_kwh: metaKwh,
      percentual_atingimento: percentualAtingimento,
      percentual_comissao: 0,
      tarifa_energia_valor: tarifaValor,
      tarifa_energia_id: tarifaId,
      valor_comissao_kwh: 0,
      valor_bonus: 0,
      salario_base: salarioBase,
      valor_ativacao: valorAtivacao,
      valor_threshold: valorThreshold,
      valor_rede_parceiros: valorRede,
      valor_bonus_safra: safra.bonus,
      // Recorrência EM ANÁLISE — zerada e sem carteira_detalhe (oculta nas telas)
      valor_recorrencia: 0,
      valor_clawback: valorClawback,
      sla_cumprido: sla.cumprido,
      sla_detalhe: slaDetalhe,
      safra_detalhe: safra.detalhe,
      carteira_detalhe: null,
      valor_total: valorTotal,
      detalhe_calculo: {
        rate_ativacao: rate,
        faixas_ativacao: ATIVACAO_FAIXAS.map(f => ({ minPct: f.minPct, rate: f.rate })),
        ativacao: memoriaAtivacao,
        threshold: { volume: volumeTotal, meta: metaKwh, valor: valorThreshold },
        sla: sla.itens,
        rede: memoriaRede,
        safra: safra.detalhe,
        clawback: clawback.itens,
        indicacoes_pos_venda: ativacao.repassesPosVenda,
      },
    },
    detalhes,
    repassesPosVenda: ativacao.repassesPosVenda,
  };
}

/**
 * Monta a rede de cada vendedor externo a partir das negociações aprovadas do mês:
 * resolve o parceiro "dono da rede" de cada negociação (parceiro ou vendedor_parceiro)
 * e agrupa volume/negociações por parceiro captado por vendedor.
 */
export async function montarRedesPorVendedor(
  allNegociacoes: NegociacaoAprovadaMes[]
): Promise<Map<string, RedeParceiroVolume[]>> {
  // Volume e negociações por parceiro (dono da rede)
  const porParceiro = new Map<string, RedeParceiroVolume>();
  for (const neg of allNegociacoes) {
    const resp = neg.responsavel;
    if (!resp) continue;
    const parceiroId = resp.role === 'parceiro' ? resp.id : resp.role === 'vendedor_parceiro' ? resp.parceiro_id : null;
    if (!parceiroId) continue;
    let entry = porParceiro.get(parceiroId);
    if (!entry) {
      entry = { parceiroId, volumeKwh: 0, negs: [] };
      porParceiro.set(parceiroId, entry);
    }
    entry.volumeKwh += Number(neg.consumo_medio_kwh) || 0;
    entry.negs.push(neg);
  }

  if (porParceiro.size === 0) return new Map();

  // Quem captou cada parceiro
  const { data: parceiros } = await supabase
    .from('profiles')
    .select('id, captado_por_id')
    .in('id', [...porParceiro.keys()]);

  const redes = new Map<string, RedeParceiroVolume[]>();
  for (const p of (parceiros || []) as any[]) {
    if (!p.captado_por_id) continue;
    const entry = porParceiro.get(p.id);
    if (!entry) continue;
    const lista = redes.get(p.captado_por_id) || [];
    lista.push(entry);
    redes.set(p.captado_por_id, lista);
  }
  return redes;
}
